import * as S from "./styled"

import { ReactComponent as CheckCircle } from "../../assets/icons/check_circle.svg"
import { ReactComponent as DownloadIcon } from "../../assets/icons/download.svg"
import { ReactComponent as WhatsappIcon } from "../../assets/icons/whatsapp.svg"
import { ReactComponent as AttentionIcon } from "../../assets/icons/attention.svg"

import { formatMoney } from "../../utils/tb/formatMoney"
import { TTicketDisposal } from "../../utils/@types/data/ticket"
import { useLocation } from "react-router-dom"
import { formatPhone } from "../../utils/tb/phone/formatPhone"
import { getAdditionalTax } from "../../utils/tb/taxes"
import getStore from "../../store"

type SummaryItem = {
  label: string
  quantity: number
  totalCents: number
}

type Props = {
  eventName?: string
  ticketsList: TTicketDisposal[]
  taxTotalCents?: number
  /** eCommerce.chargeClient do evento: false = taxa paga pelo organizador,
   *  não entra no que o comprador pagou e não deve aparecer no resumo. */
  chargeClient?: boolean
  paymentMethod?: "" | "pix" | "credit"
  buyerEmail?: string | null
  whatsappSent?: boolean
  /**
   * "purchase" (padrão): tela de compra aprovada.
   * "download": resultado do download feito pelo link público dos ingressos —
   * mostra se o PDF foi baixado ou não e oferece "Comprar mais".
   */
  variant?: "purchase" | "download"
  downloadStatus?: "done" | "failed"
  onDownload: () => void
  onWhatsapp?: () => void
  onSeeMyTickets?: () => void
  onKeepShopping: () => void
}

const MailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
)

const buildSummary = (list: TTicketDisposal[]): SummaryItem[] => {
  const map = new Map<string, SummaryItem>()

  list.forEach((t) => {
    const key = t.id ?? `${t.name}-${t.batch_name ?? ""}`
    const label = t.mesa_type ? t.name : (t.batch_name ?? t.name)
    const totalCents = Number(t.price_sell) * t.qnt

    const existing = map.get(key)
    if (existing) {
      existing.quantity += t.qnt
      existing.totalCents += totalCents
    } else {
      map.set(key, { label, quantity: t.qnt, totalCents })
    }
  })

  return Array.from(map.values()).filter((i) => i.quantity > 0)
}

const ApprovedPanel = ({
  eventName,
  ticketsList,
  taxTotalCents = 0,
  chargeClient = true,
  paymentMethod = "",
  buyerEmail,
  whatsappSent = false,
  variant = "purchase",
  downloadStatus = "done",
  onDownload,
  onWhatsapp,
  onSeeMyTickets,
  onKeepShopping,
}: Props) => {
  const lctn = useLocation()
  const summary = buildSummary(ticketsList)
  const ticketsTotal = summary.reduce((acc, i) => acc + i.totalCents, 0)
  const { event } = getStore()
  const platformTax =
    chargeClient && Number.isFinite(taxTotalCents) && taxTotalCents > 0
      ? taxTotalCents
      : 0
  // Mesma regra do OrderResume: taxa de serviço = taxa da plataforma + taxa
  // adicional do método de pagamento (calculada sobre o total dos ingressos).
  const additionalTax = getAdditionalTax({
    baseTotal: ticketsTotal,
    method: paymentMethod,
    additionalTaxCard: event?.eCommerce?.additionalTaxCard,
    additionalTaxPix: event?.eCommerce?.additionalTaxPix,
  })
  const taxCharged = platformTax + additionalTax
  // Total tem que ser o que o comprador pagou de fato — mesma regra do
  // transaction_amount em utils/tb/order.ts: taxa só entra com chargeClient.
  const total = ticketsTotal + taxCharged
  const hasSummary = summary.length > 0

  const emailToShow =
    buyerEmail && buyerEmail.trim().length > 0 ? buyerEmail : null

  if (variant === "download") {
    const failed = downloadStatus === "failed"

    return (
      <S.Wrapper $k={3} role={failed ? "alert" : "status"}>
        <S.HeroIcon
          style={
            failed ? { backgroundColor: "rgba(251, 214, 24, 0.25)" } : undefined
          }
        >
          {failed ? (
            <AttentionIcon aria-hidden="true" />
          ) : (
            <CheckCircle aria-hidden="true" />
          )}
        </S.HeroIcon>

        <S.Heading>
          <h1 style={failed ? { color: "#111827" } : undefined}>
            {failed ? "Não conseguimos baixar" : "Download concluído!"}
          </h1>
          <p>
            {failed
              ? "O download dos seus ingressos não foi concluído. Tente novamente."
              : eventName
                ? `Seus ingressos para ${eventName} foram baixados.`
                : "Seus ingressos foram baixados."}
          </p>
        </S.Heading>

        <S.Actions>
          <S.SecondaryAction
            type="button"
            onClick={onDownload}
            aria-label={
              failed
                ? "Tentar baixar os ingressos novamente"
                : "Baixar ingressos novamente"
            }
          >
            <DownloadIcon aria-hidden="true" />
            {failed ? "Tentar novamente" : "Baixar novamente"}
          </S.SecondaryAction>
          <S.WhatsappAction type="button" onClick={onKeepShopping}>
            Comprar mais
          </S.WhatsappAction>
        </S.Actions>
      </S.Wrapper>
    )
  }

  return (
    <S.Wrapper $k={3}>
      <S.HeroIcon>
        <CheckCircle aria-hidden="true" />
      </S.HeroIcon>

      <S.Heading>
        <h1>Tudo certo!</h1>
        {/* <p>Agora envie para seu WhatsApp ou baixe seus ingressos!</p> */}
      </S.Heading>

      {whatsappSent && (
        <S.WhatsappSent role="status">
          <div
            style={{
              backgroundColor: "rgba(55, 133, 88, 0.12)",
              padding: "6px 14px",
              borderRadius: 6,
            }}
          >
            {lctn.state?.buyer?.phone ? (
              <S.WhatsappSentMessage>
                <span>Ingressos enviados para </span>
                <span style={{ whiteSpace: "nowrap" }}>
                  {formatPhone(lctn.state.buyer.phone)}!
                </span>
              </S.WhatsappSentMessage>
            ) : (
              <S.WhatsappSentMessage>Ingressos enviados!</S.WhatsappSentMessage>
            )}
          </div>
          <S.WhatsappSentSubtext>
            Confira o WhatsApp em instantes.
          </S.WhatsappSentSubtext>
        </S.WhatsappSent>
      )}

      {hasSummary && (
        <S.Summary aria-label="Resumo do pedido">
          <S.SummaryEyebrow>Resumo do pedido</S.SummaryEyebrow>
          {eventName && <S.SummaryEventName>{eventName}</S.SummaryEventName>}
          <S.SummaryDivider />
          <S.SummaryItems>
            {summary.map((item, idx) => (
              <S.SummaryItem key={idx}>
                <span>
                  {item.quantity}× {item.label}
                </span>
                <span>{formatMoney(item.totalCents, true)}</span>
              </S.SummaryItem>
            ))}
            {taxCharged > 0 && (
              <S.SummaryItem>
                <span>Taxa de serviço</span>
                <span>{formatMoney(taxCharged, true)}</span>
              </S.SummaryItem>
            )}
          </S.SummaryItems>
          <S.SummaryDivider />
          <S.SummaryTotal>
            <span>Total</span>
            <span>{formatMoney(total, true)}</span>
          </S.SummaryTotal>
        </S.Summary>
      )}

      <S.Actions>
        <S.SecondaryAction
          type="button"
          onClick={onDownload}
          aria-label="Baixar ingressos em PDF"
        >
          <DownloadIcon aria-hidden="true" />
          Baixar ingressos
        </S.SecondaryAction>
        {onWhatsapp && (
          <S.WhatsappAction
            type="button"
            onClick={onWhatsapp}
            aria-label="Reenviar ingressos por WhatsApp"
          >
            <WhatsappIcon aria-hidden="true" />
            Reenviar por WhatsApp
          </S.WhatsappAction>
        )}
      </S.Actions>

      <S.TertiaryRow>
        {onSeeMyTickets && (
          <>
            <S.TertiaryLink type="button" onClick={onSeeMyTickets}>
              Ver meus ingressos →
            </S.TertiaryLink>
            <S.TertiarySep aria-hidden="true">·</S.TertiarySep>
          </>
        )}
        <S.TertiaryLink type="button" onClick={onKeepShopping}>
          Comprar mais
        </S.TertiaryLink>
      </S.TertiaryRow>

      {emailToShow && (
        <S.EmailNote>
          <MailIcon />
          <span>Confirmação enviada para {emailToShow}</span>
        </S.EmailNote>
      )}
    </S.Wrapper>
  )
}

export default ApprovedPanel
